// Where to start (spec 31 / ATL-146) API contract.
//
// Canonical copy. @atlas/api has no dependency on @atlas/shared (repo
// convention), so packages/api/src/lib/where-to-start-types.ts mirrors this
// file field for field: change both together.
//
// Gate (never a 402): free accounts get gated: true, the single best start,
// moreCount, bestForList: [] and regions: []. A free detail request for any
// start other than the ranking's best (or one of its alternatives, the same
// place under D27) gets gated: true with no hills or bands.

export type WhereToStartMode = "lobe" | "centred";
export type WhereToStartStartType = "any" | "station" | "car_park";
export type StartPointKind = "station" | "car_park";

/** Loop presets in km (Short / Day / Long). Custom is any whole km in range. */
export const LOOP_PRESETS = { short: 10, day: 16, long: 24 } as const;
export type LoopPreset = keyof typeof LOOP_PRESETS;
export const LOOP_DEFAULT_KM = 16;
export const LOOP_MIN_KM = 5;
export const LOOP_MAX_KM = 30;

/** A selected list, in the index order every `c` array and `byList` uses. */
export interface WhereToStartListRef {
  slug: string;
  name: string;
}

/** Common query parameters for /rank and /starts/:id. */
export interface WhereToStartParams {
  /** Collection slugs (1 to 20, peak lists only). */
  lists: string[];
  /** Whole km, 5 to 30; default 16. */
  loop?: number;
  mode?: WhereToStartMode;
  /** Pro only; free is answered as "any". */
  startType?: WhereToStartStartType;
  /** Origin (Near me, a gazetteer pick or the map view); absent = Anywhere. */
  lat?: number;
  lng?: number;
  /** Ranking radius around the origin in km, 2 to 30; default 7.5. */
  radius?: number;
}

export interface WhereToStartDetailParams extends WhereToStartParams {
  /** 0 to 359.99; default = the best direction. Ignored in centred mode. */
  bearing?: number;
}

// GET /where-to-start/lists
export interface WhereToStartListSummary {
  slug: string;
  name: string;
  region: string | null;
  total: number;
  remaining: number;
  /** At least one peak on this list is on the user's record. */
  started: boolean;
}

export interface WhereToStartLists {
  lists: WhereToStartListSummary[];
}

// GET /where-to-start/remaining?lists=
export interface WhereToStartRemainingPeak {
  id: string;
  slug: string;
  name: string;
  lat: number;
  lng: number;
  elevationM: number;
  /** Indexes into `lists`. */
  c: number[];
  collected: boolean;
}

export interface WhereToStartRemaining {
  lists: WhereToStartListRef[];
  peaks: WhereToStartRemainingPeak[];
  remainingCount: number;
  collectedCount: number;
}

// GET /where-to-start/rank

/** An equivalent start folded into a representative (spec 31 D27). */
export interface StartAlternative {
  id: string;
  name: string;
  kind: StartPointKind;
}

export interface StartSummary {
  id: string;
  kind: StartPointKind;
  name: string;
  placeName: string | null;
  lat: number;
  lng: number;
  /** Hills inside the best lobe (or the centred circle). */
  count: number;
  /** Best direction in degrees; null in centred mode. */
  bearing: number | null;
  /** 16-point label for `bearing` ("E"); null in centred mode. */
  direction: string | null;
  /** Per-list breakdown; a hill on two lists is in both. */
  byList: { slug: string; count: number }[];
  /** Slug with the highest count, for "Wainwrights to the E". */
  topList: string | null;
  /**
   * D27: equivalent starts of the same kind within 600 m that reach nothing
   * this one does not, ordered by name. Always present, [] when none. In the
   * detail of a start that was itself folded in, these are the rest of its
   * group (the representative included).
   */
  alternatives: StartAlternative[];
  /** alternatives.length. */
  alternativeCount: number;
}

export interface WhereToStartRegionMarker {
  name: string;
  slug: string | null;
  lat: number;
  lng: number;
  bestCount: number;
  /** Grouped starts (D27) in the region, across the whole ranking, not only the top 50. */
  startCount: number;
}

export interface WhereToStartRank {
  /** True on the free plan. */
  gated: boolean;
  /** As applied (free is always "any"). */
  startType: WhereToStartStartType;
  loopKm: number;
  mode: WhereToStartMode;
  radiusM: number;
  origin: { lat: number; lng: number; radiusKm: number } | null;
  lists: WhereToStartListRef[];
  /** Pro: up to 50. Free: the best one only. */
  starts: StartSummary[];
  /** Free: how many more Pro would show. Pro: 0. */
  moreCount: number;
  /** Pro only; [] on free. */
  bestForList: { slug: string; start: StartSummary }[];
  /** Pro + Anywhere only; [] otherwise. */
  regions: WhereToStartRegionMarker[];
}

// GET /where-to-start/starts/:id
export interface WhereToStartStartInfo extends StartSummary {
  county: string | null;
  operator: string | null;
  fee: boolean | null;
}

export interface WhereToStartHill {
  id: string;
  slug: string;
  name: string;
  elevationM: number;
  lat: number;
  lng: number;
  /** From the start. */
  distanceM: number;
  bearing: number;
  direction: string;
  /** List indexes. */
  c: number[];
  /** Inside the lobe at `bearing` (or the centred circle). */
  inside: boolean;
}

export interface WhereToStartStartDetail {
  /** True when a free user asked for a start that is not their best. */
  gated: boolean;
  /**
   * In the detail, count / bearing / direction / byList / topList describe
   * the lobe at the requested `bearing` (the best by default). Gated: no
   * counts (count 0, byList [], bearing and topList null).
   */
  start: WhereToStartStartInfo;
  radiusM: number;
  mode: WhereToStartMode;
  /** The bearing used for `inside`; default = best. Null in centred mode. */
  bearing: number | null;
  /** Null in centred mode. */
  best: { bearing: number; count: number } | null;
  runnerUp: { bearing: number; direction: string; count: number } | null;
  /** Three cumulative bands; the last equals the inside count. */
  bands: { edgeM: number; count: number }[];
  /** Every remaining hill within 2r (lobe) or loop / 4 (centred), by distance. */
  hills: WhereToStartHill[];
  lists: WhereToStartListRef[];
}

// GET /where-to-start/search?q=
export type WhereToStartSearchKind = "place" | "postcode" | "station" | "car_park" | "peak";

export interface WhereToStartSearchResult {
  kind: WhereToStartSearchKind;
  id: string | null;
  name: string;
  /** "Village · Derbyshire", "Hartsop", "Hill · 802 m". */
  detail: string | null;
  lat: number;
  lng: number;
}

export interface WhereToStartSearch {
  results: WhereToStartSearchResult[];
}
